/**
 * DevicePickerDrawer - Reusable drawer for selecting an unlinked device to assign to a unit.
 * Opens from the "Link" button in the unit edit drawer's Linked Systems section.
 */

import { useState, useEffect, useCallback } from 'react';
import { Wifi, Sun, Zap, Wind, MapPin, Loader2, AlertTriangle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/search-input';
import { Badge } from '@/components/ui/badge';
import { AdminFormDrawer } from '@/components/settings/AdminFormDrawer';
import { useDeviceLinking, type DeviceType, type AvailableDevice, type LinkConflict } from '@/hooks/useDeviceLinking';

type DeviceTypeConfig = { label: string; icon: typeof Wifi; color: string };

const DEVICE_TYPE_CONFIG: Record<DeviceType, DeviceTypeConfig> = {
  starlink: { label: 'Starlink Terminal', icon: Wifi, color: 'text-green-400' },
  solark: { label: 'Sol-Ark Inverter', icon: Sun, color: 'text-amber-400' },
  omni: { label: 'Omni GPS', icon: MapPin, color: 'text-purple-400' },
  tesla: { label: 'Tesla Charger', icon: Zap, color: 'text-blue-400' },
  hvac: { label: 'HVAC Device', icon: Wind, color: 'text-cyan-400' },
};

function getDeviceDisplayName(device: AvailableDevice, type: DeviceType): string {
  switch (type) {
    case 'starlink': return device.service_line_nickname || device.external_terminal_id || 'Unknown';
    case 'solark': return device.solark_name || 'Unnamed plant';
    case 'omni': return device.external_device_name || device.external_device_id || 'Unknown';
    case 'tesla': return device.external_charger_id || 'Unknown';
    case 'hvac': return device.external_device_id || 'Unknown';
  }
}

function getDeviceExternalId(device: AvailableDevice, type: DeviceType): string {
  switch (type) {
    case 'starlink': return device.external_terminal_id || '';
    case 'solark': return device.external_inverter_id || '';
    case 'omni': return device.external_device_id || '';
    case 'tesla': return device.external_charger_id || '';
    case 'hvac': return device.external_device_id || '';
  }
}

interface DevicePickerDrawerProps {
  open: boolean;
  onClose: () => void;
  unitId: string;
  unitCode: string;
  deviceType: DeviceType;
  onLinked: () => void;
}

interface DevicePickerContentProps extends DevicePickerDrawerProps {
  config: DeviceTypeConfig;
}

export function DevicePickerDrawer(props: DevicePickerDrawerProps) {
  const config = DEVICE_TYPE_CONFIG[props.deviceType];

  return (
    <AdminFormDrawer
      open={props.open}
      onClose={props.onClose}
      title={`Link ${config.label}`}
      description={`Select an available ${config.label.toLowerCase()} to link to ${props.unitCode}`}
    >
      {props.open && (
        <DevicePickerContent
          key={`${props.unitId}:${props.deviceType}`}
          {...props}
          config={config}
        />
      )}
    </AdminFormDrawer>
  );
}

function DevicePickerContent({ onClose, unitId, unitCode, deviceType, onLinked, config }: DevicePickerContentProps) {
  const { isLoading, isSubmitting, availableDevices, fetchAvailable, linkDevice } = useDeviceLinking();
  const [search, setSearch] = useState('');
  const [selectedDevice, setSelectedDevice] = useState<AvailableDevice | null>(null);
  const [conflict, setConflict] = useState<LinkConflict | null>(null);

  const Icon = config.icon;

  useEffect(() => {
    fetchAvailable(deviceType);
  }, [deviceType, fetchAvailable]);

  const handleSearch = useCallback((term: string) => {
    setSearch(term);
    if (term.length >= 2 || term.length === 0) {
      fetchAvailable(deviceType, term || undefined);
    }
  }, [deviceType, fetchAvailable]);

  const handleLink = async () => {
    if (!selectedDevice) return;

    const result = await linkDevice(unitId, deviceType, selectedDevice.id, conflict?.reassignable === true);

    if (result.conflict) {
      setConflict(result.conflict);
      return;
    }

    if (result.success) {
      onLinked();
      onClose();
    }
  };

  return (
    <div className="space-y-4">
        {/* Search */}
        <div className="relative">
          <SearchInput
            placeholder="Search by ID or name..."
            value={search}
            onValueChange={handleSearch}
            className="bg-background border-border"
          />
        </div>

        {/* Conflict warning */}
        {conflict && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <AlertTriangle className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
            <div className="text-sm">
              {conflict.conflictType === 'device_linked_elsewhere' ? (
                <>
                  <p className="font-medium text-amber-400">Device already linked</p>
                  <p className="text-muted-foreground mt-1">
                    This device is currently linked to <span className="font-mono font-medium text-foreground">{conflict.currentUnitCode || 'another unit'}</span>.
                    Click &quot;Reassign&quot; to move it to {unitCode}.
                  </p>
                </>
              ) : (
                <>
                  <p className="font-medium text-amber-400">{unitCode} already has a {config.label.toLowerCase()}</p>
                  <p className="text-muted-foreground mt-1">
                    Unlink the existing device from this unit before linking another one.
                    {conflict.existingDeviceExternalId && (
                      <> Existing device: <span className="font-mono text-foreground">{conflict.existingDeviceExternalId}</span>.</>
                    )}
                  </p>
                </>
              )}
            </div>
          </div>
        )}

        {/* Device list */}
        <div className="space-y-1 max-h-[400px] overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : availableDevices.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">
              No available {config.label.toLowerCase()}s found
            </div>
          ) : (
            availableDevices.map((device) => {
              const isSelected = selectedDevice?.id === device.id;
              const displayName = getDeviceDisplayName(device, deviceType);
              const externalId = getDeviceExternalId(device, deviceType);

              return (
                <button
                  key={device.id}
                  onClick={() => {
                    setSelectedDevice(isSelected ? null : device);
                    setConflict(null);
                  }}
                  className={cn(
                    'w-full flex items-center justify-between p-3 rounded-lg text-left transition-colors',
                    isSelected
                      ? 'bg-[#8FB560]/20 border border-[#8FB560]/40'
                      : 'bg-muted/30 hover:bg-muted/50 border border-transparent'
                  )}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Icon className={cn('h-4 w-4 shrink-0', config.color)} />
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{displayName}</div>
                      {externalId && (deviceType === 'solark' || externalId !== displayName) && (
                        <div className="text-xs text-muted-foreground font-mono truncate">
                          {deviceType === 'solark' ? `Plant ID: ${externalId}` : externalId}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {device.last_seen_at && (
                      <span className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(device.last_seen_at), { addSuffix: true })}
                      </span>
                    )}
                    {deviceType === 'starlink' && device.connection_status && (
                      <Badge variant={device.connection_status === 'online' ? 'default' : 'secondary'} className="text-xs">
                        {device.connection_status}
                      </Badge>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-2 border-t border-border">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button
            onClick={handleLink}
            disabled={!selectedDevice || isSubmitting || conflict?.conflictType === 'unit_has_device'}
            className="flex-1 bg-[#8FB560] hover:bg-[#8FB560]/90"
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : null}
            {conflict?.conflictType === 'unit_has_device'
              ? 'Unlink Existing First'
              : conflict?.reassignable
                ? 'Reassign'
                : 'Link Device'}
          </Button>
        </div>
    </div>
  );
}
